import { createFileRoute, useNavigate, retainSearchParams } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  listInventoryAdjustments, approveInventoryAdjustment, postInventoryAdjustment,
  previewInventoryAdjustment,
} from "@/lib/inventory-adjustments.functions";
import { runServerFn } from "@/lib/server-fn";
import { useScope } from "@/hooks/use-scope";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DataTable, type ColumnDef } from "@/components/ui/data-table";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/ui/page-header";
import { CheckCircle2, Send, Eye, Loader2, ArrowRight, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { DateRangeFilter } from "@/components/filters/date-range-filter";
import { DateFieldSelect } from "@/components/filters/date-field-select";
import { SortDirectionToggle } from "@/components/filters/sort-direction-toggle";
import { dateRangeSearchSchema, dateRangeToBounds, retainDateRangeKeys } from "@/lib/date-range-search";

export const Route = createFileRoute("/_authenticated/app/admin/inventory-adjustments")({
  validateSearch: zodValidator(dateRangeSearchSchema),
  search: { middlewares: [retainSearchParams([...retainDateRangeKeys])] },
  component: AdjustmentsAdmin,
});

const DATE_FIELD_OPTIONS = [
  { value: "created_at", label: "Created" },
  { value: "approved_at", label: "Approved" },
  { value: "posted_at", label: "Posted" },
];

type Row = {
  id: string; sku_id: string; location_id: string; lot: string | null;
  qty_delta: number; reason: string; notes: string | null;
  requires_approval: boolean; approved_at: string | null; posted_at: string | null;
  created_at: string;
};

function AdjustmentsAdmin() {
  const { tenantId, facilityId } = useScope();
  const list = useServerFn(listInventoryAdjustments);
  const approveFn = useServerFn(approveInventoryAdjustment);
  const postFn = useServerFn(postInventoryAdjustment);
  const previewFn = useServerFn(previewInventoryAdjustment);
  const qc = useQueryClient();
  const [pendingOnly, setPendingOnly] = useState(true);
  const [busy, setBusy] = useState(false);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<{
    id: string;
    currentOnHand: number;
    qtyDelta: number;
    projectedOnHand: number;
    goesNegative: boolean;
    alreadyPosted: boolean;
  } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const urlSearch = Route.useSearch();
  type S = typeof urlSearch;
  const navigate = useNavigate({ from: Route.fullPath });
  const update = (patch: Partial<S>) => navigate({ search: (prev: S) => ({ ...prev, ...patch }) });
  const sort = urlSearch.sort ?? "desc";
  const dateField = (urlSearch.dateField as "created_at" | "approved_at" | "posted_at" | undefined) ?? "created_at";
  // Default last 30d
  const effectiveStart = urlSearch.start
    ?? (urlSearch.end ? undefined : new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10));
  const { fromIso, toIso } = useMemo(
    () => dateRangeToBounds({ start: effectiveStart, end: urlSearch.end }),
    [effectiveStart, urlSearch.end],
  );

  const { data, isLoading } = useQuery({
    queryKey: ["adjustments", tenantId, facilityId, pendingOnly, fromIso, toIso, dateField, sort],
    enabled: !!tenantId,
    queryFn: () => list({ data: {
      tenantId: tenantId!, facilityId: facilityId ?? undefined, pendingOnly,
      fromIso, toIso, dateField, sort,
    } }),
  });

  const approve = async (id: string) => {
    setBusy(true);
    try {
      await runServerFn(() => approveFn({ data: { id } }), "Approve adjustment");
      toast.success("Approved");
      qc.invalidateQueries({ queryKey: ["adjustments"] });
    } finally { setBusy(false); }
  };
  const post = async (id: string) => {
    if (!confirm("Post this adjustment to the ledger?")) return;
    setBusy(true);
    try {
      await runServerFn(() => postFn({ data: { id } }), "Post adjustment");
      toast.success("Posted to inventory ledger");
      qc.invalidateQueries({ queryKey: ["adjustments"] });
    } finally { setBusy(false); }
  };

  const togglePreview = async (id: string) => {
    if (previewId === id) {
      setPreviewId(null);
      setPreviewData(null);
      return;
    }
    setPreviewId(id);
    setPreviewData(null);
    setPreviewLoading(true);
    try {
      const res = await previewFn({ data: { id } });
      setPreviewData({ id, ...res });
    } catch (e) {
      toast.error((e as Error).message);
      setPreviewId(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const cols: ColumnDef<Row, unknown>[] = [
    { id: "qty", header: "Δ Qty",
      cell: ({ row }) => (
        <span className={`tabular-nums font-medium ${row.original.qty_delta < 0 ? "text-destructive" : "text-success"}`}>
          {row.original.qty_delta > 0 ? "+" : ""}{row.original.qty_delta}
        </span>
      ),
    },
    { id: "reason", header: "Reason",
      cell: ({ row }) => <Badge variant="outline">{row.original.reason}</Badge> },
    { id: "notes", header: "Notes",
      cell: ({ row }) => <span className="text-sm line-clamp-2 max-w-[40ch]">{row.original.notes}</span> },
    { id: "status", header: "Status",
      cell: ({ row }) => {
        if (row.original.posted_at) return <Badge className="bg-success/10 text-success border-success/30" variant="outline">Posted</Badge>;
        if (row.original.requires_approval && !row.original.approved_at) return <Badge variant="outline" className="bg-warning/10 text-warning border-warning/30">Needs approval</Badge>;
        if (row.original.approved_at) return <Badge variant="outline" className="bg-blue-500/10 text-blue-700 border-blue-200">Approved</Badge>;
        return <Badge variant="outline">Ready</Badge>;
      },
    },
    { id: "created", header: "Created",
      cell: ({ row }) => <span className="text-xs text-muted-foreground">{new Date(row.original.created_at).toLocaleString()}</span> },
    { id: "actions", header: "", enableSorting: false,
      cell: ({ row }) => (
        <div className="flex justify-end gap-1">
          {!row.original.posted_at && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => togglePreview(row.original.id)}
              title="Preview impact on on-hand"
            >
              <Eye className="h-4 w-4 mr-1" />
              {previewId === row.original.id ? "Hide" : "Preview"}
            </Button>
          )}
          {row.original.requires_approval && !row.original.approved_at && !row.original.posted_at && (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => approve(row.original.id)}>
              <CheckCircle2 className="h-4 w-4 mr-1" />Approve
            </Button>
          )}
          {!row.original.posted_at && (!row.original.requires_approval || row.original.approved_at) && (
            <Button size="sm" disabled={busy} onClick={() => post(row.original.id)}>
              <Send className="h-4 w-4 mr-1" />Post
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Inventory Adjustments"
        description="Review, approve, and post manual inventory changes. Adjustments greater than 100 units require a manager's approval."
        actions={
          <div className="flex items-center gap-2">
            <Switch id="pending" checked={pendingOnly} onCheckedChange={setPendingOnly} />
            <Label htmlFor="pending" className="cursor-pointer">Pending only</Label>
          </div>
        }
      />
      <div className="px-4 sm:px-6 py-4 space-y-3">
        <div className="flex gap-2 flex-wrap items-center">
          <DateFieldSelect
            value={dateField}
            onChange={(v) => update({ dateField: v === "created_at" ? undefined : v })}
            options={DATE_FIELD_OPTIONS}
          />
          <DateRangeFilter
            value={{ start: urlSearch.start, end: urlSearch.end }}
            onChange={(next) => update({ start: next.start, end: next.end })}
          />
          <SortDirectionToggle
            value={sort}
            onChange={(s) => update({ sort: s === "desc" ? undefined : s })}
          />
        </div>
        <DataTable
          data={(data?.adjustments ?? []) as Row[]}
          columns={cols}
          isLoading={isLoading}
          empty={{ title: "No adjustments", description: "Manual inventory adjustments will appear here." }}
        />
        {previewId && (
          <div className="rounded-lg border bg-card p-4">
            <div className="flex items-center justify-between border-b pb-2 mb-3">
              <div>
                <h3 className="text-sm font-medium">Preview impact</h3>
                <p className="text-xs text-muted-foreground">
                  Reads current on-hand from the canonical projection. Posting is what writes the ledger.
                </p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => { setPreviewId(null); setPreviewData(null); }}>
                Close
              </Button>
            </div>
            {previewLoading || !previewData ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Computing…
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-4">
                  <Metric label="Current on-hand" value={previewData.currentOnHand} />
                  <ArrowRight className="h-5 w-5 text-muted-foreground shrink-0" />
                  <Metric
                    label={`Apply Δ ${previewData.qtyDelta > 0 ? "+" : ""}${previewData.qtyDelta}`}
                    value={previewData.projectedOnHand}
                    tone={previewData.goesNegative ? "danger" : previewData.qtyDelta < 0 ? "warn" : "ok"}
                  />
                </div>
                {previewData.goesNegative && (
                  <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                    <div>
                      Posting this adjustment would drive on-hand negative. The ledger
                      enforces non-negative inventory and will reject the post.
                    </div>
                  </div>
                )}
                {previewData.alreadyPosted && (
                  <p className="text-xs text-muted-foreground">
                    This adjustment has already been posted; values reflect the state at preview time.
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

function Metric({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: "ok" | "warn" | "danger";
}) {
  return (
    <div className="rounded-md border bg-background px-4 py-2 min-w-[140px]">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div
        className={
          "mt-0.5 text-2xl font-semibold tabular-nums " +
          (tone === "danger" ? "text-destructive" : tone === "warn" ? "text-warning" : "")
        }
      >
        {value.toLocaleString()}
      </div>
    </div>
  );
}